import {
  Component,
  ChangeDetectionStrategy,
  AfterViewInit,
  NgZone,
  ElementRef,
  ViewEncapsulation,
  OnDestroy,
  viewChild,
  inject,
  output,
  input,
  signal,
} from '@angular/core';

import { LoadingComponent } from '@cue/loading';
import 'leaflet';
import '@cue/active-area';
import { ImageOverlay, Marker } from 'leaflet';
import { debounce, distinctUntilChanged, from, map, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  AppState,
  ConfigService,
  floorplans,
  ResourceService,
  Schedule,
  selectedAreaInFloorplans,
  setFloorplanFilterAreaId,
} from '@assist/shared/data';
import { cueArrowRight, IconComponent } from '@cue/ui/icons';
import { AssistGeodataBM, NavigationService } from '@cue/navigation-services';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { toObservable } from '@angular/core/rxjs-interop';
import { LazyStylesService } from '@cue/lazy-styles';
import { loadNavigationLeafletStyles } from '@cue/navigation-widgets';
import { localized } from '@cue/translate';

declare var L: any;
@Component({
  selector: 'assist-new-floorplans-map',
  templateUrl: './new-floorplans-map.component.html',
  styleUrls: ['./new-floorplans-map.component.scss'],
  imports: [LoadingComponent, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class NewFloorplansMapComponent implements AfterViewInit, OnDestroy {
  private resourceService = inject(ResourceService);
  private zone = inject(NgZone);
  private configService = inject(ConfigService);
  private translocoService = inject(TranslocoService);
  private route = inject(ActivatedRoute);
  private store = inject<Store<AppState>>(Store<AppState>);
  private navigationService = inject(NavigationService);
  private lazyStyles = inject(LazyStylesService);
  breadcrumbContainer = viewChild('breadcrumbContainer', { read: ElementRef });

  ignoreSchedules = input<boolean>(false);

  resourceClicked = output<{
    schedule: Schedule;
    reservedToOther: boolean;
  }>();
  mapElement = viewChild('mapElement', { read: ElementRef });

  mapElementSignal = viewChild('mapElement');
  mapElementSignal$ = toObservable(this.mapElementSignal);

  private map: any;
  floorplans = signal<any>(null);
  selectedMapFile: string = null;
  breadcrumb = signal<any[]>([]);
  mapRendering = signal(true);
  selectedElementId = null;
  private imageOverlay!: ImageOverlay;
  private renderedLayers = new L.FeatureGroup([], {});
  private mapRenderCycle = 0;
  private mapGeodataReady = false;
  private floorplansLoading = true;
  private selectedLocationFilterKey = '';
  cueArrowRight = cueArrowRight;
  reset$ = new Subject();

  private selectedElementIcon = L.divIcon({
    html: `<svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M12.5 1C8.3645 1 5 4.46964 5 8.73438C5 10.1753 5.38704 11.5815 6.11954 12.8015L12.0731 22.6953C12.1871 22.8848 12.388 23 12.6041 23H12.6091C12.827 22.9982 13.0284 22.8795 13.1403 22.6867L18.9421 12.697C19.6342 11.5028 20 10.1325 20 8.73438C20 4.46964 16.6355 1 12.5 1ZM17.8694 12.0351L12.5947 21.1174L7.18196 12.1223C6.57225 11.1069 6.24167 9.93535 6.24167 8.73438C6.24167 5.18043 9.05375 2.28047 12.5 2.28047C15.9462 2.28047 18.7542 5.18043 18.7542 8.73438C18.7542 9.89973 18.4454 11.0413 17.8694 12.0351Z"
                        fill="#820B8A"
                    />
                    <path
                        d="M12.5 5C10.5701 5 9 6.5701 9 8.5C9 10.4176 10.5445 12 12.5 12C14.4796 12 16 10.3965 16 8.5C16 6.5701 14.4299 5 12.5 5ZM12.5 10.8411C11.2067 10.8411 10.1589 9.78983 10.1589 8.5C10.1589 7.2134 11.2134 6.15889 12.5 6.15889C13.7866 6.15889 14.8372 7.2134 14.8372 8.5C14.8372 9.77101 13.8138 10.8411 12.5 10.8411Z"
                        fill="#820B8A"
                    />
                </svg>`,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    className: 'collegue-icon',
  });
  private selectedElementMarker: Marker;
  private selectedLocationMarkers: Marker[] = [];

  ngOnInit() {
    this.route.queryParams
      .pipe(
        map((qp) => qp['resourceId']),
        tap((x) => {
          this.selectedElementId = x ?? null;
          if (this.selectedElementId == null && this.selectedElementMarker) {
            this.selectedElementMarker.removeFrom(this.map);
            this.selectedElementMarker = null;
          }
          this.colorizeLayers(this.floorplans());
        }),
        takeUntil(this.reset$)
      )
      .subscribe();

    this.store
      .select(selectedAreaInFloorplans())
      .pipe(
        takeUntil(this.reset$),
        debounce(() => this.mapElementSignal$),
        distinctUntilChanged(
          (previous, current) => previous?.id === current?.id && previous?.newFloorplan === current?.newFloorplan
        ),
        tap(() => this.startMapRendering(true)),
        switchMap((area) =>
          this.navigationService.getGeodataForAreaId(area.id).pipe(
            switchMap((geodata) => {
              if (geodata.loading) {
                return of({ area, geodata });
              }
              return from(this.setImageMap(area.newFloorplan)).pipe(map(() => ({ area, geodata })));
            })
          )
        ),
        takeUntil(this.reset$)
      )
      .subscribe(({ geodata }) => {
        if (geodata.loading) {
          return;
        }
        this.paintLayers(geodata);
        this.mapGeodataReady = true;
        this.revealMapWhenReady();
      });

    this.store
      .select(floorplans(this.configService, this.translocoService), {
        resoureTypeInfos: this.configService.value.resourceTypeInfos,
      })
      .pipe(takeUntil(this.reset$))
      .subscribe((floorplans) => {
        const selectedLocationFilterKey = this.getLocationFilterKey(floorplans?.filter?.name);
        if (selectedLocationFilterKey !== this.selectedLocationFilterKey) {
          this.selectedLocationFilterKey = selectedLocationFilterKey;
          this.startMapRendering(false);
        }

        this.floorplansLoading = floorplans.loading;
        this.breadcrumb.set(floorplans.breadcrumb);
        this.floorplans.set(floorplans);

        if (floorplans.loading) {
          return;
        }

        this.colorizeLayers(floorplans);
        this.revealMapWhenReady();
      });
  }

  private customMapControls(): void {
    const CustomZoomControl = L.Control.Zoom.extend({
      onAdd: function (map: L.Map) {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-zoom');

        this._zoomInButton = this._createButton(
          `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_2195_108)">
            <path d="M15.8045 14.8628L11.8252 10.8835C12.9096 9.55723 13.4428 7.86489 13.3144 6.15654C13.1861 4.44819 12.406 2.85452 11.1356 1.70518C9.86516 0.555838 8.20158 -0.0612406 6.48895 -0.0184194C4.77632 0.0244018 3.14566 0.723847 1.93426 1.93524C0.72287 3.14663 0.0234252 4.77729 -0.019396 6.48993C-0.0622172 8.20256 0.554862 9.86614 1.7042 11.1366C2.85354 12.407 4.44721 13.187 6.15556 13.3154C7.86392 13.4438 9.55625 12.9106 10.8825 11.8262L14.8619 15.8055C14.9876 15.9269 15.156 15.9941 15.3308 15.9926C15.5056 15.9911 15.6728 15.921 15.7964 15.7974C15.92 15.6738 15.9901 15.5066 15.9916 15.3318C15.9932 15.157 15.926 14.9886 15.8045 14.8628ZM6.66652 12.0008C5.61169 12.0008 4.58054 11.688 3.70348 11.102C2.82642 10.516 2.14283 9.68302 1.73916 8.70848C1.3355 7.73394 1.22988 6.66158 1.43567 5.62702C1.64145 4.59245 2.14941 3.64214 2.89529 2.89626C3.64117 2.15038 4.59147 1.64243 5.62604 1.43664C6.66061 1.23086 7.73296 1.33647 8.7075 1.74014C9.68204 2.14381 10.515 2.82739 11.101 3.70446C11.6871 4.58152 11.9999 5.61266 11.9999 6.6675C11.9983 8.0815 11.4359 9.43713 10.436 10.437C9.43615 11.4368 8.08052 11.9992 6.66652 12.0008Z" fill="currentColor"/>
            <path d="M8.66666 6.00036H7.33333V4.66702C7.33333 4.49021 7.26309 4.32064 7.13807 4.19562C7.01304 4.0706 6.84347 4.00036 6.66666 4.00036C6.48985 4.00036 6.32028 4.0706 6.19526 4.19562C6.07024 4.32064 6 4.49021 6 4.66702V6.00036H4.66667C4.48986 6.00036 4.32029 6.07059 4.19526 6.19562C4.07024 6.32064 4 6.49021 4 6.66702C4 6.84383 4.07024 7.0134 4.19526 7.13842C4.32029 7.26345 4.48986 7.33369 4.66667 7.33369H6V8.66702C6 8.84383 6.07024 9.0134 6.19526 9.13842C6.32028 9.26344 6.48985 9.33368 6.66666 9.33368C6.84347 9.33368 7.01304 9.26344 7.13807 9.13842C7.26309 9.0134 7.33333 8.84383 7.33333 8.66702V7.33369H8.66666C8.84347 7.33369 9.01304 7.26345 9.13806 7.13842C9.26309 7.0134 9.33333 6.84383 9.33333 6.66702C9.33333 6.49021 9.26309 6.32064 9.13806 6.19562C9.01304 6.07059 8.84347 6.00036 8.66666 6.00036Z" fill="currentColor"/>
            </g>
            </svg>`,
          'Zoom in',
          'leaflet-control-zoom-in',
          container,
          () => map.zoomIn()
        );

        this._zoomOutButton = this._createButton(
          `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_2195_9043)">
            <path d="M15.8045 14.8628L11.8252 10.8835C12.9096 9.55723 13.4428 7.86489 13.3144 6.15654C13.1861 4.44819 12.406 2.85452 11.1356 1.70518C9.86516 0.555838 8.20158 -0.0612406 6.48895 -0.0184194C4.77632 0.0244018 3.14566 0.723847 1.93426 1.93524C0.72287 3.14663 0.0234252 4.77729 -0.019396 6.48993C-0.0622172 8.20256 0.554862 9.86614 1.7042 11.1366C2.85354 12.407 4.44721 13.187 6.15556 13.3154C7.86392 13.4438 9.55625 12.9106 10.8825 11.8262L14.8619 15.8055C14.9876 15.9269 15.156 15.9941 15.3308 15.9926C15.5056 15.9911 15.6728 15.921 15.7964 15.7974C15.92 15.6738 15.9901 15.5066 15.9916 15.3318C15.9932 15.157 15.926 14.9886 15.8045 14.8628ZM6.66652 12.0008C5.61169 12.0008 4.58054 11.688 3.70348 11.102C2.82642 10.516 2.14283 9.68302 1.73916 8.70848C1.3355 7.73394 1.22988 6.66158 1.43567 5.62702C1.64145 4.59245 2.14941 3.64214 2.89529 2.89626C3.64117 2.15038 4.59147 1.64243 5.62604 1.43664C6.66061 1.23086 7.73296 1.33647 8.7075 1.74014C9.68204 2.14381 10.515 2.82739 11.101 3.70446C11.6871 4.58152 11.9999 5.61266 11.9999 6.6675C11.9983 8.0815 11.4359 9.43713 10.436 10.437C9.43615 11.4368 8.08052 11.9992 6.66652 12.0008Z" fill="currentColor"/>
            <path d="M8.66666 6.00036H4.66667C4.48986 6.00036 4.32029 6.0706 4.19526 6.19562C4.07024 6.32064 4 6.49021 4 6.66702C4 6.84383 4.07024 7.0134 4.19526 7.13843C4.32029 7.26345 4.48986 7.33369 4.66667 7.33369H8.66666C8.84347 7.33369 9.01304 7.26345 9.13806 7.13843C9.26309 7.0134 9.33333 6.84383 9.33333 6.66702C9.33333 6.49021 9.26309 6.32064 9.13806 6.19562C9.01304 6.0706 8.84347 6.00036 8.66666 6.00036Z" fill="currentColor"/>
            </g>
            </svg>`,
          'Zoom out',
          'leaflet-control-zoom-out',
          container,
          () => map.zoomOut()
        );

        map.on('zoomend zoomlevelschange', this._updateDisabled, this);
        this._updateDisabled();

        return container;
      },
      _updateDisabled: function () {
        const { _map: map } = this;
        const classNameDisabled = 'leaflet-disabled';
        if (!map) return;

        const zoomInDisabled = map.getZoom() === map.getMaxZoom();
        const zoomOutDisabled = map.getZoom() === map.getMinZoom();

        this._zoomInButton.classList.toggle(classNameDisabled, zoomInDisabled);
        this._zoomOutButton.classList.toggle(classNameDisabled, zoomOutDisabled);
      },
    });

    const ResetViewControl = L.Control.extend({
      options: { position: 'bottomright' },
      onAdd: (map) => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        const button = L.DomUtil.create('a', 'leaflet-control-button reset-view', container);
        const llBounds = map.getBounds();

        button.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <g clip-path="url(#a)" fill="currentColor">
          <path d="M16.663 8.003h5.33a.666.666 0 0 0 0-1.333h-3.721l5.525-5.525a.666.666 0 1 0-.942-.942L17.33 5.728V2.007a.666.666 0 1 0-1.333 0v5.33c0 .367.299.666.666.666ZM7.337 15.997h-5.33a.666.666 0 1 0 0 1.333h3.721L.203 22.855a.666.666 0 1 0 .942.942l5.525-5.525v3.721a.666.666 0 0 0 1.333 0v-5.33a.666.666 0 0 0-.666-.666ZM18.272 17.33h3.721a.666.666 0 0 0 0-1.333h-5.33a.666.666 0 0 0-.666.666v5.33a.666.666 0 1 0 1.333 0v-3.721l5.525 5.525a.666.666 0 0 0 .942-.942l-5.525-5.525ZM7.337 1.34a.666.666 0 0 0-.667.667v3.721L1.145.203a.666.666 0 0 0-.942.942L5.728 6.67H2.007a.666.666 0 1 0 0 1.333h5.33a.666.666 0 0 0 .666-.666v-5.33a.666.666 0 0 0-.666-.666Z"/>
        </g>
        <defs>
          <clipPath id="a">
            <path fill="currentColor" d="M0 0h24v24H0z"/>
          </clipPath>
        </defs>
      </svg>
    `;

        L.DomEvent.disableClickPropagation(button);
        L.DomEvent.on(button, 'click', () => {
          const centerOfBounds = this.imageOverlay?.getBounds();
          this.map.fitBounds(centerOfBounds);
        });

        container.title = 'Reset view';
        return container;
      },
    });

    const customZoomControl = new CustomZoomControl({ position: 'bottomright' });
    const resetViewControl = new ResetViewControl();

    this.map.addControl(resetViewControl);
    this.map.addControl(customZoomControl);
  }

  async ngAfterViewInit(): Promise<void> {
    await loadNavigationLeafletStyles(this.lazyStyles);
    await this.initMap();
    this.customMapControls();
  }

  ngAfterViewChecked() {
    if (this.breadcrumbContainer()) {
      this.breadcrumbContainer()!.nativeElement.scrollLeft = this.breadcrumbContainer()!.nativeElement.scrollWidth;
    }
  }

  navigateBreadcrumb(newArea) {
    this.store.dispatch(
      setFloorplanFilterAreaId({
        args: {
          areaId: newArea.area.id,
        },
      })
    );
  }

  private createNewLayer(areaGeoData: AssistGeodataBM) {
    const geoJson = JSON.parse(areaGeoData.geometry) as any;
    if (geoJson.properties == null) {
      geoJson.properties = {};
    }
    if (areaGeoData.typeId == 2) {
      geoJson.properties.resourceId = areaGeoData.resourceId;
    }
    const geometryType = geoJson.type;

    const geo = L.geoJSON(geoJson, {
      style: {
        color:
          areaGeoData.typeId == 3
            ? this.configService.value.design.background.area
            : this.configService.value.design.background.reservableResource.tentative,
        fill: true,
        fillOpacity: 0.4,
        fillColor:
          areaGeoData.typeId == 3
            ? this.configService.value.design.background.area
            : this.configService.value.design.background.reservableResource.tentative,
      },
      pointToLayer: (feature, latlng) => {
        if (areaGeoData.radius) {
          return new L.Circle(latlng, areaGeoData.radius);
        } else {
          return new L.Marker(latlng, {});
        }
      },
    });

    // Area
    if (areaGeoData.typeId == 3) {
      geo.on('click', (e: { layer: any }) => {
        this.zone.run(() => {
          this.areaCallback(areaGeoData.childAreaId);
        });
      });
    }

    // // Resource
    if (areaGeoData.typeId == 2) {
      geo.on('click', (e: { layer: any }) => {
        const resourceId = e.layer?.feature?.geometry?.properties?.resourceId;
        if (resourceId) {
          let found = null;
          let skip = false;
          this.floorplans().groupedRooms.forEach((gr) => {
            if (skip) {
              return;
            }
            found = gr.timelineSchedules.find((x) => (x.resource.idForFiltering == resourceId ? true : false));
            if (found) {
              skip = true;
            }
          });

          if (
            found &&
            (this.configService.value.resourceVisibilityRestriction == 1 ||
              found.resource.conditions.reservedTo != 'other')
          ) {
            this.zone.run(() => {
              if (this.ignoreSchedules() && found) {
                this.resourceClicked.emit({
                  schedule: found,
                  reservedToOther: found.reservedTo === 'other',
                });
              }

              // if (found && found.reservedTo !== 'other') {
              this.resourceClicked.emit({
                schedule: found,
                reservedToOther: found.reservedTo === 'other',
              });
              // }
            });
          } else {
            console.error('Schedule not found in the groupedResources!');
          }
        } else {
          console.error('resourceId not found in the layer!');
        }
      });
    }

    geo.addTo(this.renderedLayers);
    (geo.getLayers()[0] as any)._path.classList.add('animate-colors');
    (geo.getLayers()[0] as any)._path.addEventListener('mouseenter', () => {
      (geo.getLayers()[0] as any)._path.setAttribute(
        'originalfill',
        (geo.getLayers()[0] as any)._path.getAttribute('fill')
      );
      (geo.getLayers()[0] as any)._path.setAttribute('fill', this.configService.value.design.onHover ?? '#f2c94c');
    });

    (geo.getLayers()[0] as any)._path.addEventListener('mouseleave', () => {
      (geo.getLayers()[0] as any)._path.setAttribute(
        'fill',
        (geo.getLayers()[0] as any)._path.getAttribute('originalfill')
      );
    });
  }

  areaCallback = (childAreaId) => {
    this.store.dispatch(
      setFloorplanFilterAreaId({
        args: {
          areaId: childAreaId,
        },
      })
    );
  };

  private paintLayers(geoData) {
    if (geoData.loading) {
      return;
    }
    const existingLayers = this.renderedLayers
      .getLayers()
      .filter((x: any) => {
        if (x.getLayers) {
          return x.getLayers()[0];
        }
        return false;
      })
      .map((x: any) => x.getLayers()[0]);

    geoData.data.forEach((assistGeoData) => {
      this.createNewLayer(assistGeoData);
    });
    for (let i = 0; i < existingLayers.length; i++) {
      const foundInCurrentLayers = geoData.data.find(
        (x) => x.layerId == existingLayers[i].toGeoJSON().properties.layerId
      );
      if (!foundInCurrentLayers) {
        const foundInParent = this.renderedLayers.getLayers().find((parent: any) => {
          if (parent.getLayers) {
            return parent.getLayers()[0] == existingLayers[i];
          }
          return false;
        });
        this.renderedLayers.removeLayer(foundInParent);
      }
    }
    this.colorizeLayers(this.floorplans());
  }

  private startMapRendering(waitForGeodata: boolean) {
    this.mapRenderCycle++;
    this.mapGeodataReady = !waitForGeodata;
    this.mapRendering.set(true);
  }

  private revealMapWhenReady(renderCycle = this.mapRenderCycle) {
    if (!this.mapGeodataReady || this.floorplansLoading) {
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (renderCycle === this.mapRenderCycle && this.mapGeodataReady && !this.floorplansLoading) {
          this.zone.run(() => this.mapRendering.set(false));
        }
      });
    });
  }

  private prepareMapAndLayers() {
    this.zone.runOutsideAngular(() => {
      if (!this.map) {
        this.map = L.map(this.mapElement().nativeElement, {
          preferCanvas: false,
          center: [0, 0],
          attributionControl: false,
          zoom: 1,
          minZoom: 0,
          maxZoom: 8,
          zoomControl: false,
          crs: L.CRS.Simple,
        } as any);

        this.map.setActiveArea({
          position: 'absolute',
          top: '0px',
          left: '0px',
          right: '0px',
          height: '100%',
        });

        L.control
          .zoom({
            position: 'bottomleft',
          })
          .addTo(this.map);
        this.map.addLayer(this.renderedLayers);
      }
    });
  }

  private async initMap() {
    this.prepareMapAndLayers();
    if (this.selectedMapFile) {
      await this.setImageMap(this.selectedMapFile);
    }
  }

  private preloadImage(imageSrc: string): Promise<void> {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve();
      image.onerror = () => resolve();
      image.src = imageSrc;
      if (image.complete) {
        resolve();
      }
    });
  }

  private async setImageMap(imageSrc: string): Promise<void> {
    if (imageSrc) {
      this.clearSelectedLocationMarkers();
      if (this.selectedElementMarker) {
        this.selectedElementMarker.removeFrom(this.map);
        this.selectedElementMarker = null;
      }

      const foundArea = this.resourceService.getAreaSyncByImageUrl(imageSrc);
      await this.preloadImage(imageSrc);

      const southWest = this.map.unproject([0, foundArea!.height], 2);
      const northEast = this.map.unproject([foundArea!.width, 0], 2);
      const bounds = new L.LatLngBounds(southWest, northEast);
      if (this.imageOverlay) {
        this.imageOverlay.setUrl(imageSrc);
        this.imageOverlay.setBounds(bounds);
      } else {
        this.imageOverlay = L.imageOverlay(imageSrc, bounds, {
          zIndex: -1,
        }).addTo(this.map);
      }
      this.map.fitBounds(this.imageOverlay.getBounds(), {
        animate: false,
      });
      this.map.invalidateSize();
    }
  }

  ngOnDestroy(): void {
    this.reset$.next(null);
  }

  private findCenterPoint(vertices: [number, number][]): [number, number] {
    const points = vertices.map((x) => ({ x: x[0], y: x[1] }));
    const l = points.length;
    const result = points.reduce(
      function (center, p, i) {
        center.x += p.x;
        center.y += p.y;
        if (i === l - 1) {
          center.x /= l;
          center.y /= l;
        }
        return center;
      },
      { x: 0, y: 0 }
    );
    return [result.x, result.y];
  }

  private getLocationNames(name: string | string[] | undefined): string[] {
    return (Array.isArray(name) ? name : name ? [name] : []).filter((value) => value?.length > 0);
  }

  private getLocationFilterKey(name: string | string[] | undefined): string {
    return this.getLocationNames(name)
      .map((value) => value.toLowerCase())
      .sort()
      .join('|');
  }

  private isSelectedLocation(resource: any, floorplans: any): boolean {
    const names = this.getLocationNames(floorplans?.filter?.name).map((name) => name.toLowerCase());
    if (names.length === 0 || !resource) {
      return false;
    }

    return [
      resource.name,
      localized(resource.displayNameForApp, this.translocoService),
      localized(resource.displayName, this.translocoService),
    ]
      .filter((name): name is string => typeof name === 'string')
      .some((name) => names.includes(name.toLowerCase()));
  }

  private clearSelectedLocationMarkers() {
    this.selectedLocationMarkers.forEach((marker) => marker.removeFrom(this.map));
    this.selectedLocationMarkers = [];
  }

  private getCenterFromLayerGroup(layerGroup: any): [number, number] {
    const layer = layerGroup.getLayers().find((x) => x.feature.geometry);
    const type = layer.feature.geometry.type;

    switch (type) {
      case 'Polygon': {
        const coordinates: [number, number][] = layer.feature.geometry.coordinates[0];
        return this.findCenterPoint(coordinates);
      }
      case 'Circle': {
        const center = layer.getCenter();
        return [center.lng, center.lat];
      }
      default:
        return [0, 0];
    }
  }

  private addSelectedLocationMarker(layerGroup: any) {
    const center = this.getCenterFromLayerGroup(layerGroup);
    const latLng = L.latLng(center[1], center[0]);
    const marker = L.marker(latLng, {
      icon: this.selectedElementIcon,
      interactive: false,
      keyboard: false,
    });
    marker.addTo(this.map);
    this.selectedLocationMarkers.push(marker);
  }

  private colorizeLayers(floorplans: any) {
    if (!floorplans) {
      return;
    }

    this.clearSelectedLocationMarkers();

    this.renderedLayers.getLayers().forEach((e: any) => {
      let foundResourceId = null;
      e.eachLayer((layer) => {
        const resourceId = layer?.feature?.geometry?.properties?.resourceId;
        if (resourceId != null) {
          foundResourceId = resourceId;
        }
      });
      if (foundResourceId !== null) {
        if (floorplans.loading) {
          e.setStyle({
            fill: this.configService.value.design.background.reservableResource.tentative,
            fillColor: this.configService.value.design.background.reservableResource.tentative,
            weight: 1,
          });
        } else {
          let skip = false;
          let found = null;
          floorplans.groupedRooms.forEach((gr) => {
            if (skip) {
              return;
            }
            found = gr.timelineSchedules.find((x) => x.resource.idForFiltering === foundResourceId);

            if (found) {
              skip = true;
            }
          });
          if (
            !found ||
            (this.configService.value.resourceVisibilityRestriction != 1 &&
              found.resource.conditions.reservedTo == 'other')
          ) {
            e.getLayers()[0]._path.classList.remove('leaflet-interactive');
            e.setStyle({
              weight: 1,
              fillColor: 'white',
              color: 'white',
            });
          } else {
            e.setStyle({
              weight: 1,
              color: found.freeColor,
              fillColor: found.freeColor,
            });
            if (found.yours) {
              e.setStyle({
                weight: 4,
                color: this.configService.value.design.border.assignedToYou,
                fillColor: found.freeColor,
              });
            }
            if (found.others) {
              e.setStyle({
                weight: 4,
                color: this.configService.value.design.border.assignedToOther,
                fillColor: found.freeColor,
              });
            }

            if (this.ignoreSchedules()) {
              e.setStyle({
                weight: 1,
                color: this.configService.value.design.background.reservableResource.free,
                fillColor: this.configService.value.design.background.reservableResource.free,
              });
            }

            if (
              this.isSelectedLocation(found.resource, floorplans) &&
              this.floorplans().selectedArea.id == found.resource.mapInfos[0].areaId
            ) {
              this.addSelectedLocationMarker(e);
            }

            if (
              found.resourceId === this.selectedElementId &&
              (this.configService.value.resourceVisibilityRestriction == 1 ||
                found.resource.conditions.reservedTo == null ||
                found.resource.conditions.reservedTo == 'you')
            ) {
              if (this.floorplans().selectedArea.id == found.resource.mapInfos[0].areaId) {
                const center = this.getCenterFromLayerGroup(e);

                if (this.selectedElementMarker) {
                  this.selectedElementMarker.removeFrom(this.map);
                }
                const latLng = L.latLng(center[1], center[0]);
                this.selectedElementMarker = L.marker(latLng, {
                  icon: this.selectedElementIcon,
                  interactive: false,
                  keyboard: false,
                });
                this.selectedElementMarker.addTo(this.map);

                this.map.fitBounds(e.getBounds().pad(5), {
                  animate: true,
                });
              }
            }
          }
        }
      }
    });
  }
}
